<template>
    <Head>
        <title>Descargas - Inicio</title>
    </Head>

    <vMessagesEmergente :alertMessage="alertMessage" />

    <div>
        <section class="relative overflow-hidden bg-[#071226] px-4 pb-12 pt-10 text-white sm:px-6 lg:px-8">
            <div class="pointer-events-none absolute inset-0">
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_30%)]"></div>
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(249,115,22,0.12),transparent_30%)]"></div>
                <div class="absolute -left-20 top-0 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl animate-pulse-slow"></div>
                <div class="absolute right-0 top-10 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl animate-pulse-slow"></div>
            </div>

            <div class="relative mx-auto max-w-7xl">
                <div class="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <div class="max-w-2xl">
                        <p class="mb-3 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-300">
                            Panel interno
                        </p>
                        <h1 class="text-4xl font-black tracking-tight sm:text-5xl">Gestión de descargas</h1>
                        <p class="mt-3 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                            Crea, edita y publica archivos para el portal de distribuidores.
                        </p>
                    </div>

                    <div class="flex flex-col gap-3 sm:flex-row">
                        <button type="button" class="ts-btn-primary inline-flex items-center justify-center gap-2" @click="openModal">
                            <PlusIcon class="h-4 w-4" />
                            Crear descarga
                        </button>

                        <button
                            type="button"
                            class="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
                        >
                            <DownloadIcon class="h-4 w-4" />
                            <vDownloadCSV :data="lista_csv" :name="`descargas-portal.csv`" class="flex flex-row">
                                Exportar CSV
                            </vDownloadCSV>
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <div class="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
            <div class="ts-card !p-4 sm:!p-5">
            <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="relative w-full max-w-md">
                    <SearchIcon class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        v-model="search"
                        type="search"
                        placeholder="Buscar por nombre, archivo o descripción..."
                        class="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-500/15"
                        @input="onSearchInput"
                    />
                </div>
                <div class="flex items-center gap-3 text-sm text-slate-500">
                    <label class="inline-flex items-center gap-2">
                        <span class="font-medium">Mostrar</span>
                        <select
                            v-model.number="perPage"
                            class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                            @change="changePerPage"
                        >
                            <option :value="10">10</option>
                            <option :value="15">15</option>
                            <option :value="25">25</option>
                            <option :value="50">50</option>
                        </select>
                    </label>
                    <span class="hidden sm:inline">{{ totalRows }} resultados</span>
                </div>
            </div>

            <div class="ts-table-wrap !rounded-xl !border-0 !shadow-none">
                <table class="ts-table">
                    <thead>
                        <tr>
                            <th v-for="field in fields" :key="field.id">{{ field.value }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="7" class="!py-10 text-center text-slate-400">Cargando descargas...</td>
                        </tr>
                        <tr v-else-if="lista_download.length === 0">
                            <td colspan="7" class="!py-10 text-center text-slate-400">
                                No se encontraron descargas{{ search ? ' para tu búsqueda' : '' }}.
                            </td>
                        </tr>
                        <template v-else>
                            <tr
                                v-for="download in lista_download"
                                :key="download.id"
                                class="group"
                            >
                                <td class="max-w-[260px]">
                                    <p class="font-bold text-slate-900 truncate" :title="download.name">{{ download.name }}</p>
                                    <p class="mt-0.5 truncate text-xs text-slate-500">
                                        {{ download.description || 'Sin descripción' }}
                                    </p>
                                    <button
                                        type="button"
                                        class="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 transition hover:text-orange-700"
                                        @click="openDescriptionPanel(download)"
                                    >
                                        <AnnotationIcon class="h-3.5 w-3.5" />
                                        Ver descripción
                                    </button>
                                </td>
                                <td class="max-w-[160px]">
                                    <span class="block truncate text-sm text-slate-600" :title="fileName(download.path)">
                                        {{ fileName(download.path) }}
                                    </span>
                                </td>
                                <td>
                                    <div class="flex flex-wrap items-center gap-2">
                                        <template v-for="(brand, idx) in download.brands_rel" :key="idx">
                                            <img :src="brand.brand.image" alt="logo de marca" class="h-7 w-auto object-contain">
                                        </template>
                                    </div>
                                </td>
                                <td>
                                    <span class="text-sm font-medium text-slate-700">{{ download.employee.name }}</span>
                                </td>
                                <td>
                                    <span
                                        class="inline-flex rounded-full px-2.5 py-1 text-xs font-bold"
                                        :class="download.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'"
                                    >
                                        {{ download.active ? 'Sí' : 'No' }}
                                    </span>
                                </td>
                                <td>
                                    <span class="whitespace-nowrap text-sm text-slate-600">{{ formatDate(download.updated_at) }}</span>
                                </td>
                                <td>
                                    <div class="flex items-center gap-2">
                                        <button
                                            type="button"
                                            class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-white shadow-sm transition hover:bg-orange-600"
                                            title="Editar"
                                            @click="editDownload(download)"
                                        >
                                            <PencilAltIcon class="h-4 w-4" />
                                        </button>
                                        <button
                                            type="button"
                                            class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-600 text-white shadow-sm transition hover:bg-red-700"
                                            title="Eliminar"
                                            @click="deleteDownload(download)"
                                        >
                                            <TrashIcon class="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <div class="mt-5 flex flex-col items-center justify-between gap-3 border-t border-slate-100 pt-4 sm:flex-row">
                <p class="text-sm text-slate-500">
                    Página <span class="font-semibold text-slate-700">{{ currentPage }}</span>
                    de <span class="font-semibold text-slate-700">{{ totalPages }}</span>
                </p>
                <Paginator
                    :perPage="perPage"
                    :currentPage="currentPage"
                    :totalRows="totalRows"
                    @updateParams="paramsUpdate"
                />
            </div>
            </div>
        </div>
    </div>

    <Modal
        ref="modalCreate"
        :brands="brands"
        :user_access="user_access"
        :downloads="download"
        :title="titulo"
        @updateTable="reloadTable"
    />

    <Teleport to="body">
        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="descriptionPanel.open"
                class="fixed inset-0 z-[200] flex justify-end"
            >
                <div class="absolute inset-0 bg-slate-900/40" @click="closeDescriptionPanel"></div>
                <Transition
                    enter-active-class="transition duration-300 ease-out"
                    enter-from-class="translate-x-full"
                    enter-to-class="translate-x-0"
                    leave-active-class="transition duration-200 ease-in"
                    leave-from-class="translate-x-0"
                    leave-to-class="translate-x-full"
                >
                    <aside
                        v-if="descriptionPanel.open"
                        class="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
                    >
                        <div class="border-b border-slate-100 bg-[#071226] px-5 py-5 text-white">
                            <div class="flex items-start justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-300">Descripción</p>
                                    <h2 class="mt-1 truncate text-lg font-bold">{{ descriptionPanel.download?.name }}</h2>
                                    <p class="mt-1 truncate text-sm text-slate-300">
                                        {{ fileName(descriptionPanel.download?.path) }}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/15"
                                    @click="closeDescriptionPanel"
                                >
                                    <XIcon class="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div class="flex-1 overflow-y-auto px-5 py-5">
                            <div
                                v-if="descriptionPanel.download?.brands_rel?.length"
                                class="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                            >
                                <img
                                    v-for="(brand, idx) in descriptionPanel.download.brands_rel"
                                    :key="idx"
                                    :src="brand.brand.image"
                                    alt="logo de marca"
                                    class="h-8 w-auto object-contain"
                                >
                            </div>

                            <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                                <p class="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Detalle</p>
                                <p class="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
                                    {{ descriptionPanel.download?.description || 'Sin descripción' }}
                                </p>
                            </div>

                            <dl class="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div class="rounded-2xl border border-slate-100 px-3 py-3">
                                    <dt class="text-[11px] font-bold uppercase tracking-wide text-slate-400">Creado por</dt>
                                    <dd class="mt-1 font-medium text-slate-800">{{ descriptionPanel.download?.employee?.name }}</dd>
                                </div>
                                <div class="rounded-2xl border border-slate-100 px-3 py-3">
                                    <dt class="text-[11px] font-bold uppercase tracking-wide text-slate-400">Activa</dt>
                                    <dd class="mt-1 font-medium text-slate-800">
                                        {{ descriptionPanel.download?.active ? 'Sí' : 'No' }}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div class="border-t border-slate-100 px-5 py-4">
                            <button
                                type="button"
                                class="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                                @click="editFromDescriptionPanel"
                            >
                                <PencilAltIcon class="h-4 w-4" />
                                Editar descarga
                            </button>
                        </div>
                    </aside>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import BasePage from '@/Layouts/Base.vue';
import { Head } from '@inertiajs/inertia-vue3';
import {
    DownloadIcon,
    PlusIcon,
    PencilAltIcon,
    TrashIcon,
    SearchIcon,
    AnnotationIcon,
    XIcon,
} from '@heroicons/vue/outline';
import vDownloadCSV from 'vue-json-csv';
import vMessagesEmergente from '@/Components/Base/EmergeMessage.vue';
import Paginator from '@/Components/Base/Paginator.vue';
import Modal from '@/Components/ModalCreateDownload.vue';
import Swal from 'sweetalert2';

export default {
    layout: BasePage,
    components: {
        Head,
        Paginator,
        vMessagesEmergente,
        vDownloadCSV,
        Modal,
        PlusIcon,
        PencilAltIcon,
        TrashIcon,
        DownloadIcon,
        SearchIcon,
        AnnotationIcon,
        XIcon,
    },
    data() {
        return {
            titulo: 'Nueva descarga',
            perPage: 15,
            totalRows: 0,
            currentPage: 1,
            search: '',
            searchTimer: null,
            loading: false,
            descriptionPanel: {
                open: false,
                download: null,
            },
            alertMessage: {
                alert: 0,
                message: '',
            },
            fields: [
                { id: 1, value: 'Nombre' },
                { id: 2, value: 'Archivo' },
                { id: 3, value: 'Marcas' },
                { id: 5, value: 'Creado por' },
                { id: 6, value: 'Activa' },
                { id: 7, value: 'Fecha' },
                { id: 8, value: 'Acciones' },
            ],
            download: {
                id: 0,
                name: '',
                tempfile: '',
                filename: '',
                active: true,
                lines: [0],
                categories: [0],
                subcategories: [0],
                brands: '',
                description: '',
            },
            user: {},
            user_access: {},
            lista_download: [],
            lista_csv: [],
        };
    },
    props: {
        lines: Array | Object,
        categories: Array | Object,
        subcategories: Array | Object,
        brands: Array | Object,
    },
    computed: {
        totalPages() {
            if (!this.totalRows || !this.perPage) return 1;
            return Math.max(1, Math.ceil(this.totalRows / this.perPage));
        },
    },
    methods: {
        openDescriptionPanel(download) {
            this.descriptionPanel = {
                open: true,
                download,
            };
        },
        closeDescriptionPanel() {
            this.descriptionPanel.open = false;
        },
        editFromDescriptionPanel() {
            const item = this.descriptionPanel.download;
            this.closeDescriptionPanel();
            if (item) {
                this.editDownload(item);
            }
        },
        emptyDownload() {
            return {
                id: 0,
                name: '',
                tempfile: '',
                filename: '',
                active: true,
                lines: [0],
                categories: [0],
                subcategories: [0],
                brands: '',
                description: '',
            };
        },
        openModal() {
            this.titulo = 'Nueva descarga';
            this.download = this.emptyDownload();
            this.$nextTick(() => {
                this.$refs.modalCreate.showModal();
            });
        },
        log(type, place, message) {
            switch (type) {
                case 'info':
                    console.info(`${place}: Text. ${message}`);
                    break;
                case 'error':
                    console.error(`Error :  ${place}: Text. ${message}`);
                    break;
            }
        },
        fileName(path) {
            if (!path) return '';
            return path.substring(path.indexOf('/') + 1);
        },
        editDownload(data) {
            this.titulo = 'Editar descarga';
            this.download = {
                ...data,
                active: data.active == 1,
                tempfile: '',
                filename: '',
                brands: data.brands_rel?.[0]?.brand_id || '',
            };
            this.$nextTick(() => {
                this.$refs.modalCreate.showModal();
            });
        },
        deleteDownload(download) {
            Swal.fire({
                title: '¿Eliminar esta descarga?',
                text: 'Esta acción no podrá ser revertida.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ea580c',
                cancelButtonColor: '#64748b',
                confirmButtonText: 'Eliminar',
                cancelButtonText: 'Cancelar',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.actionDelete(download);
                }
            });
        },
        actionDelete(download) {
            axios.post(route('delete.download'), {
                download_id: download.id,
            }).then((response) => {
                let data = response.data;
                if (data.status == true) {
                    Swal.fire({
                        title: 'Eliminado',
                        text: 'El archivo ha sido eliminado.',
                        icon: 'success',
                        timer: 1800,
                        showConfirmButton: false,
                    });
                    this.get_list();
                } else {
                    Swal.fire({
                        text: 'Ocurrió un error, intenta nuevamente.',
                        icon: 'warning',
                    });
                }
            }).catch((error) => {
                this.log('error', 'delete', error);
            });
        },
        get_list() {
            this.loading = true;
            let options = {
                perPage: this.perPage,
                currentPage: this.currentPage,
                search: this.search,
            };
            axios.post(route('list.download'), options, {
                headers: {
                    'X-CSRF-TOKEN': this.$page.props.csrf_token,
                },
            })
                .then((response) => {
                    let data = response.data;
                    if (data.status == true) {
                        this.totalRows = data.list.total;
                        this.lista_download = data.list.data;
                    }
                }).catch((error) => {
                    this.log('error', 'saveDownload', error);
                }).finally(() => {
                    this.loading = false;
                });
        },
        get_list_csv() {
            axios.get(route('csv.list'), {
                headers: {
                    'X-CSRF-TOKEN': this.$page.props.csrf_token,
                },
            })
                .then((response) => {
                    let list = response.data.list;
                    this.lista_csv = [];
                    for (let index in list) {
                        let download = list[index];
                        let brands = '';
                        for (let ib in download.brands_rel) {
                            brands += download.brands_rel[ib].brand.name + '\n';
                        }

                        this.lista_csv.push({
                            id: download.id,
                            nombre: download.name,
                            archivo: this.fileName(download.path),
                            marcas: brands,
                            ingeniero: download.employee.name,
                            activa: download.active ? 'Si' : 'No',
                            editado: this.formatDate(download.updated_at),
                        });
                    }
                }).catch((error) => {
                    this.log('error', 'saveDownload', error);
                });
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            let options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: false,
            };
            return new Intl.DateTimeFormat('es-MX', options).format(date);
        },
        paramsUpdate(value) {
            this.currentPage = value;
            this.get_list();
        },
        onSearchInput() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.currentPage = 1;
                this.get_list();
            }, 350);
        },
        changePerPage() {
            this.currentPage = 1;
            this.get_list();
        },
        reloadTable() {
            this.get_list();
            this.get_list_csv();
        },
    },
    mounted() {
        this.user = this.$page.props.user;
        this.user_access = this.$page.props.access;
        this.get_list();
        this.get_list_csv();
    },
};
</script>
